import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import MultiStepModal from '../MultiStepModal';
import { videoReferenceSelectedForAlignmentCheckAtom } from '../../atoms/videoAlignmentRequired';
import {
  canProceedAtom,
  currentStepAtom,
  isModalOpenAtom,
  playbackAndMinutesOkAtom,
  resetMultiStepAtoms,
  alignmentNotesAtom,
  draftIsAlignedAtom,
} from '../../atoms/videoAlignmentCheck';
import useApi, { API_ENDPOINTS } from '../../hooks/api';
import RenderGate from '../RenderGate';
import { ReviewEventStep } from './steps/ReviewEventStep';
import { ReviewOffsetsStep } from './steps/ReviewOffsetsStep';
import { offsetDataIsUpdatingAtom } from '../../atoms/review';

const stepTitles = (intl) => ({
  0: intl.formatMessage({
    defaultMessage: 'Review Offsets',
    id: '62cf2',
  }),
  1: intl.formatMessage({
    defaultMessage: 'Review Event',
    id: 'ac45f',
  }),
});

export function VideoAlignmentCheck({ onFinalConfirm }) {
  const intl = useIntl();
  const {
    patch,
    isFetching: isSaving,
    errorMessage,
    clearErrorMessage,
  } = useApi();

  const [currentStep, setCurrentStep] = useAtom(currentStepAtom);

  const isOpen = useAtomValue(isModalOpenAtom);
  const canProceed = useAtomValue(canProceedAtom);

  const videoReferenceSelectedForAlignmentCheck = useAtomValue(
    videoReferenceSelectedForAlignmentCheckAtom,
  );

  const isAligned = useAtomValue(draftIsAlignedAtom);
  const playbackAndMinutesOk = useAtomValue(playbackAndMinutesOkAtom);
  const alignmentNotes = useAtomValue(alignmentNotesAtom);

  const offsetDataIsUpdating = useAtomValue(offsetDataIsUpdatingAtom);

  const resetFormState = useSetAtom(resetMultiStepAtoms);

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleClose = () => {
    resetFormState();
    clearErrorMessage();
  };

  const handleFinalConfirm = async () => {
    const { sport, matchId, period, track, version } =
      videoReferenceSelectedForAlignmentCheck || {};

    const data = {
      alignment_notes: isAligned ? null : alignmentNotes,
      is_aligned: isAligned,
      playback_and_minutes_ok: isAligned ? null : playbackAndMinutesOk,
    };

    const result = await patch(
      `${API_ENDPOINTS.videoService}/${sport}/${matchId}/${period}/${track}/${version}/video-info`,
      { data: JSON.stringify(data) },
    );

    if (!result) return;

    onFinalConfirm(result);
    resetFormState();
  };

  if (!isOpen) return null;

  return (
    <RenderGate
      isFetching={false}
      errorMessage={errorMessage}
      toastBlocksRender={false}
      onClose={clearErrorMessage}
    >
      <MultiStepModal
        id="review-offsets-modal"
        title={stepTitles(intl)[currentStep]}
        isOpen={isOpen}
        steps={[<ReviewOffsetsStep />, <ReviewEventStep />]}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        canProceed={canProceed}
        onConfirm={handleFinalConfirm}
        onClose={handleClose}
        onNext={handleNext}
        onPrevious={handlePrevious}
        isConfirmPending={isSaving}
        isDisabledEscape={offsetDataIsUpdating}
      />
    </RenderGate>
  );
}

VideoAlignmentCheck.propTypes = {
  onFinalConfirm: PropTypes.func,
};

VideoAlignmentCheck.defaultProps = {
  onFinalConfirm: () => {},
};

export default VideoAlignmentCheck;
